import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import type { GenericList } from './models/JobSharedInterfaces';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';
import ConfirmPopup from './ConfirmPopup';

interface AddShutdownPeriod {
    startDate: string;
    endDate: string;
    chargeId: number;
    subId: string;
}

interface ShutdownPeriodList {
    id: number;
    startDateFormatted: number;
    endDateFormatted: string;
    chargeType: string;
}

const ShutdownPeriodData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [shutdownPeriodTypes, setShutdownPeriodTypes] = React.useState<GenericList[]>([]);
    const initShutdownPeriod: AddShutdownPeriod = {
        startDate: "",
        endDate: "",
        chargeId: -1,
        subId: ""
    };
    const [addShutdownPeriod, setAddShutdownPeriod] = React.useState<AddShutdownPeriod>(initShutdownPeriod);
    const [shutdownPeriods, setShutdownPeriods] = React.useState<ShutdownPeriodList[]>([]);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [deletePeriodId, setDeletePeriodId] = React.useState(0);
    const [deletePeriodIndex, setDeletePeriodIndex] = React.useState(0);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateEditAdminLists");
        if (hasPermission) {
            getShutdownPeriods();
        } else {
            navigate("/accessdenied");
        }
    }


    const getShutdownPeriods = async () => {
        const apiUrl = "GetShutdownPeriods";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setShutdownPeriods(result.data.shutdownPeriods);
            setShutdownPeriodTypes(result.data.shutdownPeriodTypes);
            setLoading(false);
        } 
    }

    const clearError = (errorName: string) => {
        var errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleAddChange = (e: any) => {
        clearError(e.target.name);

        setAddShutdownPeriod(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleAddChangeNumber = (e: any) => {
        clearError(e.target.name);

        setAddShutdownPeriod(prevState => ({ ...prevState, [e.target.name]: Number(e.target.value) }));
    }

    const validateAdd = () => {
        let formIsValid = true;
        let errorList: any = {};

        let fields = addShutdownPeriod;
        if (!fields.startDate) {
            errorList["startDate"] = "Start Date is Required";
            formIsValid = false;
        } else if (!DateHelpers.isValidDate(fields.startDate)) {
            errorList["startDate"] = "Valid Start Date is Required";
            formIsValid = false;
        }

        if (!fields.endDate) {
            errorList["endDate"] = "End Date is Required";
            formIsValid = false;
        } else if (!DateHelpers.isValidDate(fields.endDate)) {
            errorList["endDate"] = "Valid End Date is Required";
            formIsValid = false;
        }

        if (fields.chargeId < 0) {
            errorList["chargeId"] = "Charge Type is Required";
            formIsValid = false;
        }

        if (formIsValid) {
            //check if end date > start date
            //check to make sure install date is before or equal to offhire date
            if (!DateHelpers.compareDateGreater(fields.startDate, fields.endDate)) {
                errorList["endDate"] = "End Date must be > Start Date, ";
                formIsValid = false;
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = () => {
        if (validateAdd()) {
            saveShutdownPeriods();
        }
    }

    const saveShutdownPeriods = async () => {
        var jsonItem = JSON.stringify(addShutdownPeriod);

        const apiUrl = "SaveShutdownPeriod";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Shutdown Period Saved");
            //then need to clear holiday so can save a new one
            var period: AddShutdownPeriod = {
                startDate: "",
                endDate: "",
                chargeId: -1,
                subId: ""
            };

            setAddShutdownPeriod(period);
            getShutdownPeriods();
        }
    }

    const deleteItem = (e: any, delPeriodId: number, delPeriodIndex: number) => {
        e.preventDefault();
        setDeletePeriodId(delPeriodId);
        setDeletePeriodIndex(delPeriodIndex);
        setShowConfirmModal(true);
    }

    const hideConfirmModal = () => {
        setDeletePeriodId(0);
        setDeletePeriodIndex(0);
        setShowConfirmModal(false);
    }

    const saveConfirmModal = () => {
        saveDeleteShutdownPeriod();
    }

    const saveDeleteShutdownPeriod = async () => {
        var shutdowns = [...shutdownPeriods];
        var shutdownPeriod = {
            shutdownPeriodId: deletePeriodId
        };

        var jsonItem = JSON.stringify(shutdownPeriod);

        const apiUrl = "DeleteShutdownPeriod";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Shutdown Period Deleted Successfully");
            shutdowns.splice(deletePeriodIndex, 1);
            setShutdownPeriods(shutdowns);
            setDeletePeriodId(0);
            setDeletePeriodIndex(0);
            setShowConfirmModal(false);
        }
    }

    const confirmPopup = <ConfirmPopup heading="Delete Shutdown Period?" text="Are you sure you want to delete this shutdown period?" hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
            </div>
            <div>
                <div>
                    <span>Enter dates for when the office shut downs over the Christmas period.<br /></span>
                    <span>Customers: in the Customer Admin screen, on the Customer Rules tab select which hire period customers will not be charged equipment hire for. See below for rules/days<br /><br /></span>

                    <span>Charge Type: No Charge Part Shutdown<br /></span>
                    <span>The part shutdown period is generally Christmas day to New Years day. This means that any customers (most of them) will not be charged equipment hire during this period <br /><br /></span>
                    <span>Charge Type: No Charge Full Shutdown<br /></span>
                    <span>The full shutdown period is generally when the office is shut down.  Generally a few days before christmas and after new years day.  Only a few customers do not get charged equipment hire for this period.<br /><br /></span>
                </div>
                <label className="input-group" htmlFor="startDate">
                    <span className="label">Start Date</span>
                    <input type='date' id="startDate" name="startDate" className='input' value={addShutdownPeriod.startDate} onChange={(e) => handleAddChange(e)}></input>
                </label>
                <span className="label errors textalignleft marginLeft10">{errors["startDate"]}</span>

                <label className="input-group" htmlFor="endDate">
                    <span className="label">End Date</span>
                    <input type='date' id="endDate" name="endDate" className='input' value={addShutdownPeriod.endDate} onChange={(e) => handleAddChange(e)}></input>
                </label>
                <span className="label errors textalignleft marginLeft10">{errors["endDate"]}</span>

                <label className="input-group" htmlFor="chargeId">
                    <span className="label">Charge Type</span>
                    <select className="select" id="chargeId" name="chargeId" value={addShutdownPeriod.chargeId} onChange={(e) => handleAddChangeNumber(e)}>
                        <option hidden defaultValue="-1"></option>
                        {shutdownPeriodTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <span className="label errors">{errors["chargeId"]}</span>

                <button className="defaultbutton defaultbutton__small marginLeftLabel" onClick={save} type="button">Add</button>
            </div>
            <div>
                <hr></hr>
            </div>
            <div className="marginBottom20">
                <div className="job__noteGridSize overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Charge Type</th>
                                <th></th>
                            </tr>
                        </thead>
                        {shutdownPeriods.map((period, index) =>
                            <tbody key={period.id}>
                                <tr>
                                    <td className="hidden">{period.id}</td>
                                    <td>{period.startDateFormatted}</td>
                                    <td>{period.endDateFormatted}</td>
                                    <td>{period.chargeType}</td>
                                    <td>
                                        <div className="delete--tablecell">
                                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, period.id, index)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return <div>
        <h1>Shutdown Periods</h1>
        {contents}
    </div>;
};

export default ShutdownPeriodData;